/**
 * XRAnchorSystem — Tracks XR anchors and syncs ECS entity transforms.
 *
 * Phase: RenderSub, SubPhase: Update, Priority: 5
 * Active only during AR sessions with 'anchors' feature granted.
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EventBus } from '../../core/EventBus.js';
import type { XRSessionState } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { NeedsSync } from '../../ecs/Component.js';
import { Logger } from '../../core/Logger.js';

export class XRAnchorSystem implements ISystem {
  readonly name = 'XRAnchor';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.Update;
  readonly priority = 5;

  private _state: XRSessionState;
  private _events: EventBus;
  // Two scratch sets that swap every frame. Previously a fresh Set was
  // allocated each execute() — at 90 Hz that is 90 Set objects/second
  // turning into GC pressure on mobile AR devices.
  private _previousAnchors = new Set<XRAnchor>();
  private _currentAnchors = new Set<XRAnchor>();
  private _anchorSession: XRSession | null = null;
  private _warnedMissingFrame = false;

  constructor(state: XRSessionState, events: EventBus) {
    this._state = state;
    this._events = events;
  }

  execute(_dt: number, world: World): void {
    if (!this._state.isPresenting) return;
    if (!this._state.hasFeature('anchors')) return;

    // Reset stale anchors when session changes
    if (this._state.session !== this._anchorSession) {
      this._previousAnchors.clear();
      this._currentAnchors.clear();
      this._anchorSession = this._state.session;
    }

    const frame = (globalThis as any).__xrFrame as XRFrame | undefined;
    if (!frame) {
      // One-shot warning: something is wiring up XR but the frame global
      // isn't being set (see App.ts animation loop). Without this, all XR
      // frame-data systems silently no-op and the failure is invisible.
      if (!this._warnedMissingFrame) {
        Logger.warn('XRAnchor', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    const trackedAnchors = frame.trackedAnchors;
    if (!trackedAnchors) return;

    const refSpace = this._state.referenceSpace;
    if (!refSpace) return;

    // Reuse the scratch set rather than allocating anew.
    this._currentAnchors.clear();

    // Update tracked anchors
    for (const anchor of trackedAnchors) {
      this._currentAnchors.add(anchor);

      // Detect new anchors
      if (!this._previousAnchors.has(anchor) && !this._state.anchorIdMap.has(anchor)) {
        const id = this._state.nextAnchorId();
        this._state.anchorIdMap.set(anchor, id);
        this._events.emit('xr:anchor-created', { anchorId: id });
      }

      // Update entity transform if mapped
      const entityId = this._state.anchorEntityMap.get(anchor);
      if (entityId !== undefined && world.isAlive(entityId)) {
        try {
          const anchorPose = frame.getPose(anchor.anchorSpace, refSpace);
          if (anchorPose) {
            const obj = world.getObject3D(entityId);
            obj.matrix.fromArray(anchorPose.transform.matrix);
            obj.matrix.decompose(obj.position, obj.quaternion, obj.scale);
            // Tag the entity so downstream systems (physics kinematics,
            // AI tactical queries) re-read the fresh transform on the
            // next tick instead of operating on stale ECS state.
            world.addTag(entityId, NeedsSync);
          }
        } catch (err) {
          // Anchor may have become invalid. Debug-level so it doesn't
          // spam the console but still surfaces on a headset devtools
          // connection when investigating drop-outs.
          Logger.debug('XRAnchor', `getPose failed for anchor ${entityId}: ${err}`);
        }
      }
    }

    // Detect lost anchors
    for (const anchor of this._previousAnchors) {
      if (!this._currentAnchors.has(anchor)) {
        const id = this._state.anchorIdMap.get(anchor);
        if (id !== undefined) {
          this._events.emit('xr:anchor-lost', { anchorId: id });
          this._state.anchorIdMap.delete(anchor);
        }
        this._state.anchorEntityMap.delete(anchor);
      }
    }

    // Swap — `_previousAnchors` becomes the fresh set, the old one becomes
    // the scratch for next frame's writes. Zero allocation per frame.
    const tmp = this._previousAnchors;
    this._previousAnchors = this._currentAnchors;
    this._currentAnchors = tmp;
  }

  dispose(): void {
    this._previousAnchors.clear();
    this._currentAnchors.clear();
    this._anchorSession = null;
  }
}
